import React, { useEffect, useRef, useState } from 'react';
import './TicketLotteryModal.css';
import { saveTicketLotteryEntry } from '../Services/ticketLotteryService';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

export default function TicketLotteryModal({ open, onClose, event, onEntered }) {
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (open) document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setEmail('');
      setErrorMsg('');
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === dialogRef.current) onClose?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!event?.id) {
      setErrorMsg('Missing event details. Please try again.');
      return;
    }
    if (!isValidEmail(email)) {
      setErrorMsg('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    const result = saveTicketLotteryEntry({
      eventId: event.id,
      eventName: event.name,
      email,
    });
    if (!result.ok) {
      setErrorMsg(result.message || 'Unable to save your entry.');
      setSubmitting(false);
      return;
    }
    setSuccess(true);
    setSubmitting(false);
    onEntered?.();
    setTimeout(() => {
      onClose?.();
    }, 1200);
  };

  if (!open) return null;

  return (
    <div
      className="ticket-lottery-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ticket-lottery-title"
      ref={dialogRef}
      onMouseDown={handleOverlayClick}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="ticket-lottery-modal"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="ticket-lottery-close"
          aria-label="Close waitlist"
          onClick={onClose}
          type="button"
          disabled={submitting}
        >
          ×
        </button>

        {success ? (
          <div className="ticket-lottery-success">You are on the waitlist!</div>
        ) : (
          <>
            <h2 id="ticket-lottery-title" className="ticket-lottery-title">
              Join the Waitlist for:
            </h2>
            <p className="ticket-lottery-event">{event?.name || 'this event'}</p>
            <p className="ticket-lottery-subtitle">
              If the promoter releases special ticket offers, you will be on a list to be notified to claim the offer. No payment is required unless you claim a ticket.
            </p>

            <form className="ticket-lottery-form" onSubmit={handleSubmit}>
              <label className="ticket-lottery-label">
                Email
                <input
                  className="ticket-lottery-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </label>

              {errorMsg && <p className="ticket-lottery-error">{errorMsg}</p>}

              <button className="ticket-lottery-submit" type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Join the Waitlist'}
              </button>
            </form>

            <p className="ticket-lottery-footnote">
              By joining, you agree to be contacted about ticket availability and payment if selected.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
